import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, CheckCircle, AlertCircle, Info, X,
  Trash2, Settings, Filter, CheckSquare, DollarSign, Briefcase, 
  CreditCard, Lock, MessageSquare
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { notificationsAPI } from '../services/api';
import { BackHeader } from '../components/BackHeader';

type NotificationType = 'expense' | 'project' | 'payment' | 'closure' | 'system' | 'message' | 'condo_request' | string;
type NotificationPriority = 'low' | 'medium' | 'high';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  priority: NotificationPriority;
  read: boolean;
  createdAt?: string;
  created_at?: string;
  actionUrl?: string;
  actionText?: string;
  url?: string | null;
  meta?: {
    amount?: string;
    requestor?: string;
    status?: string;
  };
}

function normalizeNotification(n: any): Notification {
  let dataUrl: string | undefined;
  let d = n?.data;
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch (_) { d = null; } }
  if (d && typeof d === 'object' && typeof d.url === 'string') dataUrl = d.url;
  return {
    ...n,
    read: !!n.read,
    message: n.message ?? n.body ?? '',
    createdAt: n.createdAt ?? n.created_at,
    actionUrl: n.actionUrl ?? n.url ?? dataUrl,
  };
}

// Deep-link (2026-10-20): extract the announcement id an announcement
// notification points at (data.announcementId / announcement_id; data may be
// JSON-encoded depending on the DB driver). Null when the row carries none.
function getAnnouncementDeepLinkId(n: any): string | null {
  let d = n?.data;
  if (!d) return null;
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch (_) { return null; } }
  const id = d?.announcementId || d?.announcement_id || null;
  return id ? String(id) : null;
}

// Deep-link (2026-10-20): stamp ?announcement=<id> on the target so
// Announcements.tsx opens the View Modal for THAT announcement, not just the
// condo's list. Mirrors the stamping in components/Notifications.tsx.
function stampAnnouncementParam(target: string, notification: any): string {
  const announcementId = getAnnouncementDeepLinkId(notification);
  if (!announcementId) return target;
  try {
    const stamped = new URL(target, window.location.origin);
    stamped.searchParams.set('announcement', announcementId);
    return stamped.pathname + stamped.search + stamped.hash;
  } catch { return target; }
}

// Mock data removed — page fetches real data from notificationsAPI

export default function NotificationsCenter() {
  console.info('vecindus-notif-fix-20260918a'); // build marker
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread' | NotificationType>('all');
  const [selectedType, setSelectedType] = useState<NotificationType | 'all'>('all');

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationsAPI.getAll();
      const data = response.data?.data || response.data || response || [];
      setNotifications(Array.isArray(data) ? data.map(normalizeNotification) : []);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread' && n.read) return false;
    if (selectedType !== 'all' && n.type !== selectedType) return false;
    if (filter !== 'all' && filter !== 'unread' && n.type !== filter) return false;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const handleDelete = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleClearAll = () => {
    if (confirm('¿Eliminar todas las notificaciones?')) {
      setNotifications([]);
    }
  };

  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case 'expense': return <DollarSign className="w-5 h-5" />;
      case 'project': return <Briefcase className="w-5 h-5" />;
      case 'payment': return <CreditCard className="w-5 h-5" />;
      case 'closure': return <Lock className="w-5 h-5" />;
      case 'message': return <MessageSquare className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: NotificationType) => {
    switch (type) {
      case 'expense': return 'bg-amber-100 text-amber-700';
      case 'project': return 'bg-blue-100 text-blue-700';
      case 'payment': return 'bg-emerald-100 text-emerald-700';
      case 'closure': return 'bg-purple-100 text-purple-700';
      case 'message': return 'bg-[#36CCD8]/10 text-[#36CCD8]';
      default: return 'bg-[var(--v-surface-2)] text-[var(--v-text-primary)]';
    }
  };

  const getPriorityColor = (priority: NotificationPriority) => {
    switch (priority) {
      case 'high': return 'border-l-red-500';
      case 'medium': return 'border-l-amber-500';
      default: return 'border-l-gray-300';
    }
  };

  const getTimeAgo = (dateString?: string | number) => {
    if (dateString === undefined || dateString === null || dateString === '') return '';
    let date: Date;
    if (typeof dateString === 'number') {
      date = new Date(dateString < 1e12 ? dateString * 1000 : dateString);
    } else {
      const raw = String(dateString).trim();
      if (/^\d+$/.test(raw)) {
        const num = Number(raw);
        date = new Date(num < 1e12 ? num * 1000 : num);
      } else {
        // Postgres "YYYY-MM-DD HH:MM:SS" is not reliably parseable; use ISO form (local time).
        date = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
      }
    }
    if (isNaN(date.getTime())) return '';
    const diff = Date.now() - date.getTime();
    if (diff < 0) return 'ahora';
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (minutes < 1) return 'ahora';
    if (minutes < 60) return `${minutes} m`;
    if (hours < 24) return `${hours} h`;
    return `${days} d`;
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <BackHeader fallback="/" />
      {/* Header */}
      <header className="sticky top-0 bg-[var(--v-surface)] border-b border-[var(--v-border)] z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            {/* B2 FIX (2026-10-19, Jaime report): this second back button duplicated
                the shared <BackHeader> above (double back affordance). Removed —
                BackHeader stays the single back path, matching Announcements,
                Calendar and StaffDirectory. */}
            <div>
              <h1 className="text-xl font-bold text-[var(--v-text-primary)]">Notificaciones</h1>
              <p className="text-xs text-[var(--v-text-secondary)]">{unreadCount} sin leer</p>
            </div>
          </div>
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <button 
                onClick={handleMarkAllAsRead}
                className="px-3 py-2 text-sm font-medium text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-xl flex items-center gap-1"
              >
                <CheckSquare className="w-4 h-4" /> Marcar todas
              </button>
            )}
            <button 
              onClick={handleClearAll}
              className="p-2 text-[var(--v-text-tertiary)] hover:text-red-500 hover:bg-red-50 rounded-xl"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-2xl mx-auto">
        {/* Filters */}
        <div className="bg-[var(--v-surface)] rounded-2xl border border-[var(--v-border)] shadow-sm p-4 mb-6">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <button 
              onClick={() => { setFilter('all'); setSelectedType('all'); }}
              className={`px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all ${
                filter === 'all' && selectedType === 'all' 
                  ? 'bg-[#36CCD8] text-white' 
                  : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)]'
              }`}
            >
              Todas
            </button>
            <button 
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all ${
                filter === 'unread' 
                  ? 'bg-[#36CCD8] text-white' 
                  : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)]'
              }`}
            >
              Sin leer {unreadCount > 0 && `(${unreadCount})`}
            </button>
            <button 
              onClick={() => setSelectedType('expense')}
              className={`px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all flex items-center gap-1 ${
                selectedType === 'expense' 
                  ? 'bg-amber-100 text-amber-700' 
                  : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)]'
              }`}
            >
              <DollarSign className="w-4 h-4" /> Gastos
            </button>
            <button 
              onClick={() => setSelectedType('payment')}
              className={`px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all flex items-center gap-1 ${
                selectedType === 'payment' 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)]'
              }`}
            >
              <CreditCard className="w-4 h-4" /> Pagos
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {filteredNotifications.map(notification => (
            <div 
              key={notification.id}
              onClick={(e) => {
                // Nested buttons (Marcar como leída / trash / action) handle themselves.
                if ((e.target as HTMLElement).closest('button')) return;
                if (!notification.read) handleMarkAsRead(notification.id);
                const target = notification.actionUrl;
                if (target) navigate(stampAnnouncementParam(target, notification));
              }}
              role="button"
              style={{ cursor: 'pointer' }}
              className={`bg-[var(--v-surface)] rounded-2xl border border-[var(--v-border)] shadow-sm overflow-hidden transition-all hover:shadow-md ${
                !notification.read ? 'bg-blue-50/30' : ''
              } ${getPriorityColor(notification.priority)} border-l-4`}
            >
              <div className="p-5">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getTypeColor(notification.type)}`}>
                    {getTypeIcon(notification.type)}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className={`font-bold text-white truncate ${!notification.read ? 'text-white' : 'text-[var(--v-text-secondary)]'}`}>
                        {notification.title}
                      </h3>
                      <span className="text-xs text-[var(--v-text-tertiary)] whitespace-nowrap">
                        {getTimeAgo(notification.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-[var(--v-text-secondary)] mt-1">{notification.message}</p>
                    
                    {/* Meta */}
                    {notification.meta && (
                      <div className="flex items-center gap-3 mt-2">
                        {notification.meta.amount && (
                          <span className="text-sm font-bold text-[#36CCD8]">{notification.meta.amount}</span>
                        )}
                        {notification.meta.status && (
                          <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                            notification.meta.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            notification.meta.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                            'bg-[var(--v-surface-2)] text-[var(--v-text-primary)]'
                          }`}>
                            {notification.meta.status}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3">
                      {!notification.read && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notification.id); }}
                          className="px-3 py-1.5 text-xs font-medium text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-lg transition-colors"
                        >
                          Marcar como leída
                        </button>
                      )}
                      {notification.actionUrl && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); if (notification.actionUrl) navigate(stampAnnouncementParam(notification.actionUrl, notification)); }}
                          className="px-3 py-1.5 text-xs font-medium bg-[#36CCD8] text-white hover:bg-[#2DB8C4] rounded-lg transition-colors"
                        >
                          {notification.actionText}
                        </button>
                      )}
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDelete(notification.id); }}
                        className="ml-auto p-1.5 text-[var(--v-text-tertiary)] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {filteredNotifications.length === 0 && (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 mx-auto mb-4 text-[var(--v-text-tertiary)]" />
              <p className="text-[var(--v-text-secondary)]">No hay notificaciones</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}


